/**
 * EbuyProfilePanel — Reusable SIN profile editor for the GSA eBuy
 * Sentinel. The same component is mounted in two places:
 *
 *  1. Workspace Settings → Integrations  (contractor self-edit; no org id)
 *  2. Client Profile (consultant edits SINs on behalf of a client; org id
 *     passed in)
 *
 * Backend endpoints: GET / PUT  /api/ebuy/profile[?organization_id=...]
 *
 * v3.18.23 — GSA eBuy Sentinel.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Loader2, CheckCircle2, Plus, X, Shield, Sparkles, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ChipList({ values, onRemove, emptyText, testIdBase }) {
  if (!values || values.length === 0) {
    return <span className="text-xs text-slate-400 italic">{emptyText}</span>;
  }
  return values.map((v, idx) => (
    <Badge
      key={`${v}-${idx}`}
      className="bg-slate-100 text-slate-700 border border-slate-200 px-3 py-1 text-sm"
      data-testid={`${testIdBase}-chip-${v}`}
    >
      {v}
      <button
        type="button"
        onClick={() => onRemove(idx)}
        className="ml-2 hover:text-slate-900"
        data-testid={`${testIdBase}-remove-${v}`}
      >
        <X className="w-3 h-3" />
      </button>
    </Badge>
  ));
}

function ChipInput({ label, placeholder, values, onChange, testIdBase, helperText }) {
  const [input, setInput] = useState('');
  const add = () => {
    const v = input.trim();
    if (!v) return;
    if (values.includes(v)) {
      toast.info(`${v} is already in the list.`);
      return;
    }
    onChange([...values, v]);
    setInput('');
  };
  return (
    <div className="space-y-3">
      <Label className="flex items-center gap-2">{label}</Label>
      {helperText && <p className="text-xs text-slate-500">{helperText}</p>}
      <div className="flex gap-2">
        <Input
          placeholder={placeholder}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          className="flex-1"
          data-testid={`${testIdBase}-input`}
        />
        <Button
          type="button"
          variant="outline"
          onClick={add}
          className="border-cyan-200 text-cyan-700 hover:bg-cyan-50"
          data-testid={`${testIdBase}-add-btn`}
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <div className="flex flex-wrap gap-2 min-h-[32px]">
        <ChipList
          values={values}
          onRemove={(idx) => onChange(values.filter((_, i) => i !== idx))}
          emptyText="None added yet"
          testIdBase={testIdBase}
        />
      </div>
    </div>
  );
}

export default function EbuyProfilePanel({ organizationId = null, clientName = null }) {
  const { authToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sinCodes, setSinCodes] = useState([]);
  const [relatedSins, setRelatedSins] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [outputPath, setOutputPath] = useState('');

  const buildUrl = useCallback((base) => {
    return organizationId ? `${base}?organization_id=${encodeURIComponent(organizationId)}` : base;
  }, [organizationId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(buildUrl(`${API_URL}/api/ebuy/profile`), {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setSinCodes(body.gsa_sin_codes || []);
      setRelatedSins(body.gsa_related_sin_codes || []);
      setContracts(body.gsa_schedule_contracts || []);
      setOutputPath(body.ebuy_output_path || '');
    } catch (e) {
      console.error('eBuy profile load failed', e);
      toast.error('Failed to load eBuy profile.');
    } finally {
      setLoading(false);
    }
  }, [authToken, buildUrl]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(buildUrl(`${API_URL}/api/ebuy/profile`), {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          gsa_sin_codes: sinCodes,
          gsa_related_sin_codes: relatedSins,
          gsa_schedule_contracts: contracts,
          ebuy_output_path: outputPath,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || `HTTP ${res.status}`);
      }
      toast.success(`eBuy SIN profile saved${clientName ? ` for ${clientName}` : ''}.`);
    } catch (e) {
      toast.error(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="ebuy-profile-panel">
      <CardHeader className="pb-4">
        <CardTitle
          className="text-lg font-semibold text-slate-900 flex items-center gap-2"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          <Sparkles className="w-5 h-5 text-cyan-700" />
          GSA eBuy SIN Profile {clientName && <span className="text-slate-400 font-normal text-sm">· {clientName}</span>}
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          These codes drive Gold / Silver / Bronze match tiers on the GSA eBuy Sentinel tab.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading ? (
          <div className="flex items-center justify-center h-24">
            <Loader2 className="w-5 h-5 text-cyan-700 animate-spin" />
          </div>
        ) : (
          <>
            <ChipInput
              label={<><Shield className="w-3 h-3" /> SIN Codes — Gold match (your awarded SINs)</>}
              placeholder="e.g. 54151S"
              values={sinCodes}
              onChange={setSinCodes}
              testIdBase="ebuy-sin"
              helperText="Special Item Numbers your firm is awarded on. Exact match → Gold tier."
            />
            <ChipInput
              label={<><Shield className="w-3 h-3" /> Related SINs — Silver match (adjacent capabilities)</>}
              placeholder="e.g. 54151HACS"
              values={relatedSins}
              onChange={setRelatedSins}
              testIdBase="ebuy-related-sin"
              helperText="SINs you can credibly team / partner on. Match → Silver tier."
            />
            <ChipInput
              label={<><Shield className="w-3 h-3" /> Schedule Contract Numbers (Optional)</>}
              placeholder="e.g. GS-35F-XXXXX"
              values={contracts}
              onChange={setContracts}
              testIdBase="ebuy-contract"
              helperText="Used to validate which RFQs you can respond to. Informational only."
            />

            {/* eBuy Output Folder Path for disk-based sync */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <FolderOpen className="w-3 h-3" />
                eBuy Output Folder Path
              </Label>
              <p className="text-xs text-slate-500">
                Absolute path to the local eBuy scraper output folder (e.g.{' '}
                <code className="bg-slate-100 px-1 rounded text-xs">/path/to/ebuy_tool/output</code>).
                The server reads state.json and attachment files directly from this path when you click "Sync from Disk".
              </p>
              <Input
                placeholder="/absolute/path/to/ebuy_tool/output"
                value={outputPath}
                onChange={(e) => setOutputPath(e.target.value)}
                className="font-mono text-sm"
                data-testid="ebuy-output-path-input"
              />
            </div>

            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h4 className="text-sm font-medium text-slate-700 mb-2">eBuy Tier Logic</h4>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-amber-400"></span>
                  <strong>Gold (50%):</strong> RFQ category matches one of your SIN codes
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-slate-400"></span>
                  <strong>Silver (30%):</strong> RFQ category matches a Related SIN
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-orange-400"></span>
                  <strong>Bronze (10%):</strong> No SIN match
                </li>
              </ul>
            </div>

            <Button
              onClick={save}
              disabled={saving}
              className="w-full bg-cyan-700 hover:bg-cyan-800 text-white"
              data-testid="ebuy-profile-save-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              Save SIN Profile
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
